const LOADING_INDICATOR_DELAY_MS = 200; // heuristic value
const NAV_STALL_THRESHOLD_MS = 5000; // heuristic value
const MAX_CHUNK_LOAD_RETRIES = 2; // heuristic value
const NAV_STALL_EVENT = 'nav-stall';

interface NavStallDetail {
  fromPathname: string;
  waitedMs: number;
}

interface RspackChunkLoadError extends Error {
  type?: 'timeout' | 'error' | 'missing';
  request?: string;
}

function isChunkLoadError(error: unknown): error is RspackChunkLoadError {
  return error instanceof Error && error.name === 'ChunkLoadError';
}

/**
 * rspack の runtime は、失敗（timeout を含む）した chunk の状態を戻す。
 * そのため、import を呼び直せば新しい request が出る。
 */
async function retryOnChunkLoadError<T>(
  factory: () => Promise<T>,
  maxRetries = MAX_CHUNK_LOAD_RETRIES,
): Promise<T> {
  let attempt = 0;
  while (true) {
    try {
      return await factory();
    } catch (error) {
      if (!isChunkLoadError(error) || attempt >= maxRetries) {
        throw error;
      }
      attempt += 1;
    }
  }
}

async function resolveWithin<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => resolve(undefined), ms);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

function notifyIfNotSettledWithin(promise: Promise<unknown>, ms: number, onTimeout: () => void): void {
  const timer = setTimeout(onTimeout, ms);
  const clear = () => clearTimeout(timer);
  promise.then(clear, clear);
}

export type { NavStallDetail, RspackChunkLoadError };
export {
  isChunkLoadError,
  LOADING_INDICATOR_DELAY_MS,
  MAX_CHUNK_LOAD_RETRIES,
  NAV_STALL_EVENT,
  NAV_STALL_THRESHOLD_MS,
  notifyIfNotSettledWithin,
  resolveWithin,
  retryOnChunkLoadError,
};
